import { useState, useEffect, useContext, useRef } from 'react';
import { 
    Box, 
    Typography, 
    Button,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Autocomplete,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    Alert
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridToolbar } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StorageIcon from '@mui/icons-material/Storage';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import CloudIcon from '@mui/icons-material/Cloud';
import ComputerIcon from '@mui/icons-material/Computer';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PreviewIcon from '@mui/icons-material/Preview';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PeopleIcon from '@mui/icons-material/People';
import TEXT from './text';
import { AppGlobalContext } from '../../../App';
import { fieldSelector } from '../../../utils/fieldSelector';
import { DragDropContext } from 'react-beautiful-dnd';
import { useYjsRecords, useYjsDatabases, useYjsDbGroups, SyncStatus } from '../../../services/yjs';
import { SyncStatusBadge, PendingChangesIndicator } from '../../Yjs';

const DbRecords = () => {
    const language = useSelector(state => state.App_Settings.language);
    const userEmail = useSelector(state => state.UserMetaData.email);
    const userUid = useSelector(state => state.UserMetaData.uid);
    const AppGlobalContxt = useContext(AppGlobalContext);
    
    const [database, setDatabase] = useState(null);
    const [dbGroup, setDbGroup] = useState(null);

    // Yjs custom hooks for databases and templates
    const { databases: yjsDatabases } = useYjsDatabases({ uid: userUid });
    const { dbGroups: yjsDbGroups } = useYjsDbGroups({ uid: userUid });

    const [editingRecord, setEditingRecord] = useState(null);
    const [viewMenuAnchor, setViewMenuAnchor] = useState(null);
    const [selectedViewRecord, setSelectedViewRecord] = useState(null);
    const [quickViewOpen, setQuickViewOpen] = useState(false);
    const [quickViewRecord, setQuickViewRecord] = useState(null);
    const [fieldItems, setFieldItems] = useState([]);
    const [permissionsOpen, setPermissionsOpen] = useState(false);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [userSearchResults, setUserSearchResults] = useState([]);
    const [searchingUsers, setSearchingUsers] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedPermissionType, setSelectedPermissionType] = useState('');
    const [updatingPermission, setUpdatingPermission] = useState(false);
    
    // Yjs integration state via custom hook wrapping Yjs state
    const tableName = database?.name;
    const {
        records: yjsRecords,
        addRecord: addYjsRecord,
        updateRecord: updateYjsRecord,
        deleteRecord: deleteYjsRecord,
        isHydrated,
        isConnected,
        isSynced,
        pendingChanges
    } = useYjsRecords(tableName, {
        dbId: database?.id,
        roomName: database?.id ? `db-${database.id}` : undefined,
        uid: userUid,
        initialRecords: database?.records
    });

    const syncStatus = !navigator.onLine 
        ? SyncStatus.OFFLINE 
        : isSynced 
            ? SyncStatus.SYNCED 
            : isConnected 
                ? SyncStatus.CONNECTED 
                : SyncStatus.CONNECTING;
    const [lastSyncTime, setLastSyncTime] = useState(null);

    // Helper functions for fieldSelector
    const get_item_by_id = (id) => {
        return fieldItems.find(item => item.id === id);
    };

    const get_item_index_by_id = (id) => {
        return fieldItems.findIndex(item => item.id === id);
    };

    // Load database from localStorage or context
    useEffect(() => {
        const loadDatabase = () => {
            const selectedDbId = localStorage.getItem('selectedDatabaseId');
            if (!selectedDbId) {
                console.log('[DBRECORDS] No database selected');
                return;
            }

            // First, open without version to check current state
            const checkRequest = indexedDB.open('ShepherdDB');
            
            checkRequest.onsuccess = (event) => {
                const db = event.target.result;
                const currentVersion = db.version;
                const needsUpgrade = !db.objectStoreNames.contains('DBgroups') || 
                                     !db.objectStoreNames.contains('Databases');
                
                // Check if DBgroups has the name index
                let needsIndex = false;
                if (db.objectStoreNames.contains('DBgroups')) {
                    try {
                        const transaction = db.transaction(['DBgroups'], 'readonly');
                        const objectStore = transaction.objectStore('DBgroups');
                        needsIndex = !objectStore.indexNames.contains('name');
                    } catch (e) {
                        console.warn('[DBRECORDS] Could not check for index:', e);
                    }
                }
                
                db.close();
                
                let dbRequest;
                
                if (needsUpgrade || needsIndex) {
                    // Need to upgrade, open with new version
                    console.log('[DBRECORDS] Database needs upgrade, opening with version', currentVersion + 1);
                    dbRequest = indexedDB.open('ShepherdDB', currentVersion + 1);
                    
                    dbRequest.onupgradeneeded = (event) => {
                        const db = event.target.result;
                        
                        // Create DBgroups object store if it doesn't exist
                        if (!db.objectStoreNames.contains('DBgroups')) {
                            const dbGroupStore = db.createObjectStore('DBgroups', { keyPath: 'id', autoIncrement: true });
                            dbGroupStore.createIndex('name', 'name', { unique: false });
                            console.log('[DBRECORDS] Created DBgroups object store with name index');
                        } else if (needsIndex) {
                            // Add index to existing store
                            const transaction = event.target.transaction;
                            const objectStore = transaction.objectStore('DBgroups');
                            if (!objectStore.indexNames.contains('name')) {
                                objectStore.createIndex('name', 'name', { unique: false });
                                console.log('[DBRECORDS] Added name index to existing DBgroups');
                            }
                        }
                        
                        // Create Databases object store if it doesn't exist
                        if (!db.objectStoreNames.contains('Databases')) {
                            db.createObjectStore('Databases', { keyPath: 'id', autoIncrement: true });
                            console.log('[DBRECORDS] Created Databases object store');
                        }
                    };
                } else {
                    // Everything exists, open normally without version
                    console.log('[DBRECORDS] Database exists, opening normally');
                    dbRequest = indexedDB.open('ShepherdDB');
                }
                
                const queryDbFromStore = (dbStore, db, onFound, onDone) => {
                    // Try numeric key if it's integer
                    const numId = Number(selectedDbId);
                    if (!isNaN(numId) && Number.isInteger(numId) && String(numId) === String(selectedDbId)) {
                        const getReq = dbStore.get(numId);
                        getReq.onsuccess = () => {
                            if (getReq.result) {
                                onFound(getReq.result);
                            } else {
                                scanAllDbs(dbStore, onFound, onDone);
                            }
                        };
                        getReq.onerror = () => {
                            scanAllDbs(dbStore, onFound, onDone);
                        };
                    } else {
                        // If selectedDbId is string or NaN, scan all records
                        scanAllDbs(dbStore, onFound, onDone);
                    }
                };

                const scanAllDbs = (dbStore, onFound, onDone) => {
                    const getAllReq = dbStore.getAll();
                    getAllReq.onsuccess = () => {
                        const allDbs = getAllReq.result || [];
                        const matched = allDbs.find(d => String(d.id) === String(selectedDbId) || d.name === selectedDbId);
                        if (matched) {
                            onFound(matched);
                        } else {
                            onDone();
                        }
                    };
                    getAllReq.onerror = () => onDone();
                };

                dbRequest.onsuccess = (event) => {
                    const db = event.target.result;
                    console.log('[DBRECORDS] IndexedDB opened successfully, version:', db.version);
                    
                    if (!db.objectStoreNames.contains('Databases')) {
                        db.close();
                        return;
                    }

                    const dbTransaction = db.transaction(['Databases'], 'readonly');
                    const dbStore = dbTransaction.objectStore('Databases');
                    
                    queryDbFromStore(dbStore, db, (loadedDb) => {
                        setDatabase({
                            ...loadedDb,
                            recordsCount: loadedDb.records?.length || 0
                        });
                        
                        if (db.objectStoreNames.contains('DBgroups')) {
                            const groupTransaction = db.transaction(['DBgroups'], 'readonly');
                            const groupStore = groupTransaction.objectStore('DBgroups');
                            
                            if (groupStore.indexNames.contains('name')) {
                                const groupIndex = groupStore.index('name');
                                const getGroupRequest = groupIndex.get(loadedDb.dbGroupName);
                                getGroupRequest.onsuccess = () => {
                                    setDbGroup(getGroupRequest.result);
                                    db.close();
                                };
                                getGroupRequest.onerror = () => db.close();
                            } else {
                                const getAllGroups = groupStore.getAll();
                                getAllGroups.onsuccess = () => {
                                    const matchedGroup = (getAllGroups.result || []).find(g => g.name === loadedDb.dbGroupName);
                                    if (matchedGroup) setDbGroup(matchedGroup);
                                    db.close();
                                };
                                getAllGroups.onerror = () => db.close();
                            }
                        } else {
                            db.close();
                        }
                    }, () => {
                        console.log('[DBRECORDS] Database not found in IndexedDB');
                        db.close();
                    });
                };
                
                dbRequest.onerror = (error) => {
                    console.error('[DBRECORDS] Error opening IndexedDB:', error);
                };
            };
            
            checkRequest.onerror = (error) => {
                console.error('[DBRECORDS] Error checking IndexedDB:', error);
            };
        };

        loadDatabase();
    }, []);

    // Sync database and dbGroup from Yjs hooks as primary/fallback source of truth
    useEffect(() => {
        const selectedDbId = localStorage.getItem('selectedDatabaseId');
        if (!selectedDbId) return;

        if (!database && yjsDatabases && yjsDatabases.length > 0) {
            const foundDb = yjsDatabases.find(d => String(d.id) === String(selectedDbId) || d.name === selectedDbId);
            if (foundDb) {
                setDatabase(prev => prev || {
                    ...foundDb,
                    recordsCount: foundDb.records?.length || 0
                });
            }
        }
    }, [database, yjsDatabases]);

    useEffect(() => {
        if (database && !dbGroup && yjsDbGroups && yjsDbGroups.length > 0) {
            const foundGroup = yjsDbGroups.find(g => g.name === database.dbGroupName);
            if (foundGroup) {
                setDbGroup(prev => prev || foundGroup);
            }
        }
    }, [database, dbGroup, yjsDbGroups]);

    // Check user permission for a database
    const getUserPermission = (db) => {
        if (!db) return 'none';
        if (!userUid || db.storageType === 'local') {
            console.log('[DBRECORDS] Permission check: Local database or no userUid, granting owner');
            return 'owner'; // Local databases have full access
        }
        
        const checkUid = (list) => {
            if (!list) return false;
            // Convert userUid to string for comparison and handle both string and number formats
            const userIdStr = String(userUid).trim();
            const listArray = list.split(',').map(id => String(id).trim());
            console.log('[DBRECORDS] Checking if', userIdStr, 'is in', listArray);
            return listArray.includes(userIdStr);
        };
        
        console.log('[DBRECORDS] Checking permissions for user:', userUid, 'Type:', typeof userUid);
        console.log('[DBRECORDS] Database owners:', db.owners, 'Type:', typeof db.owners);
        console.log('[DBRECORDS] Database admins:', db.admins, 'Type:', typeof db.admins);
        console.log('[DBRECORDS] Database contributors:', db.contributors);
        console.log('[DBRECORDS] Database readers:', db.readers);
        console.log('[DBRECORDS] Database supervised:', db.supervised);
        
        if (checkUid(db.owners)) {
            console.log('[DBRECORDS] User is OWNER');
            return 'owner';
        }
        if (checkUid(db.admins)) {
            console.log('[DBRECORDS] User is ADMIN');
            return 'admin';
        }
        if (checkUid(db.contributors)) {
            console.log('[DBRECORDS] User is CONTRIBUTOR');
            return 'contributor';
        }
        if (checkUid(db.supervised)) {
            console.log('[DBRECORDS] User is SUPERVISED');
            return 'supervised';
        }
        if (checkUid(db.readers)) {
            console.log('[DBRECORDS] User is READER');
            return 'reader';
        }
        console.log('[DBRECORDS] User has NO permission');
        return 'none';
    };

    const canEdit = (db) => {
        const permission = getUserPermission(db);
        const result = permission === 'owner' || permission === 'admin' || permission === 'supervised';
        console.log(`[DBRECORDS] canEdit check: permission=${permission}, result=${result}`);
        return result;
    };

    const canAdd = (db) => {
        const permission = getUserPermission(db);
        return permission === 'owner' || permission === 'admin' || permission === 'contributor' || permission === 'supervised';
    };

    const canDelete = (db) => {
        const permission = getUserPermission(db);
        return permission === 'owner' || permission === 'admin' || permission === 'supervised';
    };

    const canManagePermissions = (db) => {
        const permission = getUserPermission(db);
        return permission === 'owner' || permission === 'admin';
    };

    const isSupervised = (db) => {
        const permission = getUserPermission(db);
        return permission === 'supervised';
    };

    // Debounce database state changes and persist to IndexedDB
    // This ensures that Yjs updates (both local and remote) are always eventually
    // consistent inside ShepherdDB's Databases store, keeping Redux and list views accurate.
    useEffect(() => {
        if (!database) return;
        
        const saveToIndexedDB = () => {
            const dbRequest = indexedDB.open('ShepherdDB');
            dbRequest.onsuccess = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains('Databases')) {
                    db.close();
                    return;
                }
                try {
                    const transaction = db.transaction(['Databases'], 'readwrite');
                    const objectStore = transaction.objectStore('Databases');
                    objectStore.put(database);
                    transaction.oncomplete = () => {
                        db.close();
                        console.log('[DBRECORDS] Auto-saved database state to IndexedDB');
                    };
                    transaction.onerror = () => {
                        db.close();
                        console.error('[DBRECORDS] Error auto-saving database state to IndexedDB');
                    };
                } catch (e) {
                    db.close();
                    console.error('[DBRECORDS] Exception during IndexedDB auto-save:', e);
                }
            };
        };

        const timer = setTimeout(saveToIndexedDB, 500);
        return () => clearTimeout(timer);
    }, [database]);

    const saveDatabase = async (updatedDatabase) => {
        // Update local state
        setDatabase(updatedDatabase);
        
        // Update IndexedDB (keep for local persistence)
        const dbRequest = indexedDB.open('ShepherdDB');
        dbRequest.onsuccess = (event) => {
            const db = event.target.result;
            const transaction = db.transaction(['Databases'], 'readwrite');
            const objectStore = transaction.objectStore('Databases');
            objectStore.put(updatedDatabase);
            transaction.oncomplete = () => {
                db.close();
                console.log('[DBRECORDS] Database updated in IndexedDB');
            };
            transaction.onerror = () => {
                db.close();
                console.error('[DBRECORDS] Error updating database in IndexedDB');
            };
        };
        dbRequest.onerror = () => {
            console.error('[DBRECORDS] Error opening IndexedDB for save');
        };
        
        // NOTE: Yjs handles real-time sync automatically via WebSocket
        // No need for manual /push_database calls - version conflicts are eliminated
        console.log('[DBRECORDS] Database saved locally. Yjs will sync automatically.');
    };

    const handleAddRecord = () => {
        if (!database || !canAdd(database)) {
            alert(TEXT.NoPermission?.[language] || "You don't have permission to perform this action");
            return;
        }
        
        if (!dbGroup) {
            alert(TEXT.DbGroupNotFound?.[language] || 'Database template not found');
            return;
        }
        
        // Initialize form with empty values and fieldItems
        const initialData = {};
        const items = [];
        let itemId = 0;
        
        dbGroup.fields?.forEach(field => {
            initialData[field.name] = '';
            items.push({
                id: itemId++,
                name: field.name,
                type: field.type,
                properties: field.properties || {},
                value: field.value || '',
                valid: !field.mandatory
            });
        });
        
        setFieldItems(items);
        setEditingRecord({ isNew: true });
    };

    const handleEditRecordInline = (record, index) => {
        if (!canEdit(database)) {
            alert(TEXT.NoPermission?.[language] || "You don't have permission to perform this action");
            return;
        }
        
        const { id, ...recordData } = record;
        setEditingRecord({ ...recordData, index });
    };

    const handleDeleteRecord = async (index) => {
        if (!canDelete(database)) {
            alert(TEXT.NoPermission?.[language] || "You don't have permission to perform this action");
            return;
        }
        
        const confirmed = window.confirm(TEXT.ConfirmDelete?.[language] || 'Are you sure you want to delete this record?');
        if (!confirmed) return;
        
        console.log('[DBRECORDS] 🗑️ Deleting record via useYjsRecords hook at index:', index);
        deleteYjsRecord(index);
        alert(TEXT.RecordDeleted?.[language] || 'Record deleted successfully');
    };

    const handleSaveRecord = async () => {
        if (!dbGroup) return;
        console.log('[DBRECORDS] Saving record with fieldItems:', fieldItems);
        // Extract data from fieldItems
        const recordData = {};
        fieldItems.forEach(item => {
            recordData[item.name] = item.value;
        });
        
        // Validate mandatory fields
        const invalidItems = fieldItems.filter(item => {
            const field = dbGroup.fields?.find(f => f.name === item.name);
            return field?.mandatory && !item.valid;
        });
        
        if (invalidItems.length > 0) {
            alert(`${TEXT.FillAllFields?.[language] || 'Please fill all mandatory fields'}: ${invalidItems.map(i => i.name).join(', ')}`);
            return;
        }
        
        console.log('[DBRECORDS] handleSaveRecord called');
        
        if (editingRecord?.isNew) {
            console.log('[DBRECORDS] Adding new record via useYjsRecords hook');
            addYjsRecord(recordData);
        } else {
            console.log('[DBRECORDS] Updating record via useYjsRecords hook at index:', editingRecord.index);
            updateYjsRecord(editingRecord.index, recordData);
        }
        
        setEditingRecord(null);
        setFieldItems([]);
        alert(TEXT.RecordSaved?.[language] || 'Record saved successfully');
    };

    const handleProcessRowUpdate = async (newRow, oldRow) => {
        const { id, ...newRecord } = newRow;
        const index = id - 1;
        
        if (!dbGroup) return oldRow;
        
        const missingFields = dbGroup.fields?.filter(field => 
            field.mandatory && (!newRecord[field.name] || String(newRecord[field.name]).trim() === '')
        );
        
        if (missingFields && missingFields.length > 0) {
            alert(TEXT.FillAllFields?.[language] || 'Please fill all mandatory fields');
            return oldRow;
        }
        
        console.log('[DBRECORDS] ✏️ Updating row via useYjsRecords hook at index:', index);
        updateYjsRecord(index, newRecord);
        return newRow;
    };

    const handleBackToBrowser = () => {
        localStorage.removeItem('selectedDatabaseId');
        AppGlobalContxt.Body.setPageName('DbBrowser');
    };

    const handleViewRecordClick = (event, record, index) => {
        setViewMenuAnchor(event.currentTarget);
        setSelectedViewRecord({ ...record, index });
    };

    const handleCloseViewMenu = () => {
        setViewMenuAnchor(null);
    };

    const handleQuickViewRecord = () => {
        setQuickViewRecord(selectedViewRecord);
        setQuickViewOpen(true);
        handleCloseViewMenu();
    };

    const handleSearchUsers = async (query) => {
        if (!query || query.trim().length < 2) {
            setUserSearchResults([]);
            return;
        }

        setSearchingUsers(true);
        try {
            const url = window.devMode ? 'http://localhost:80/search_users' : '/search_users';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: userEmail,
                    query: query.trim()
                })
            });

            const result = await response.json();

            if (response.ok) {
                setUserSearchResults(result.users || []);
            } else {
                console.error('[DBRECORDS] Error searching users:', result);
                alert(result.error || 'Error searching users');
            }
        } catch (error) {
            console.error('[DBRECORDS] Network error searching users:', error);
            alert('Network error searching users');
        } finally {
            setSearchingUsers(false);
        }
    };

    const handleAddUserToPermission = async () => {
        if (!selectedUser || !selectedPermissionType) {
            alert(TEXT.SelectPermissionType?.[language] || 'Please select a user and permission type');
            return;
        }

        setUpdatingPermission(true);
        try {
            const url = window.devMode ? 'http://localhost:80/update_database_permissions' : '/update_database_permissions';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: userEmail,
                    database_name: database.name,
                    db_group_name: database.dbGroupName,
                    permission_type: selectedPermissionType,
                    action: 'add',
                    target_uid: selectedUser.uid
                })
            });

            const result = await response.json();

            if (response.ok) {
                // Update local database state
                setDatabase({
                    ...database,
                    owners: result.permissions.owners.join(','),
                    admins: result.permissions.admins.join(','),
                    contributors: result.permissions.contributors.join(','),
                    readers: result.permissions.readers.join(','),
                    supervised: result.permissions.supervised.join(','),
                    version: result.version
                });

                // Clear selections
                setSelectedUser(null);
                setUserSearchQuery('');
                setUserSearchResults([]);

                alert(TEXT.PermissionUpdated?.[language] || 'Permission updated successfully');
            } else {
                console.error('[DBRECORDS] Error updating permission:', result);
                alert(result.error || 'Error updating permission');
            }
        } catch (error) {
            console.error('[DBRECORDS] Network error updating permission:', error);
            alert('Network error updating permission');
        } finally {
            setUpdatingPermission(false);
        }
    };

    const handleRemoveUserFromPermission = async (uid, permissionType) => {
        const confirmed = window.confirm(`Remove user ${uid} from ${permissionType}?`);
        if (!confirmed) return;

        setUpdatingPermission(true);
        try {
            const url = window.devMode ? 'http://localhost:80/update_database_permissions' : '/update_database_permissions';
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: userEmail,
                    database_name: database.name,
                    db_group_name: database.dbGroupName,
                    permission_type: permissionType,
                    action: 'remove',
                    target_uid: uid
                })
            });

            const result = await response.json();

            if (response.ok) {
                // Update local database state
                setDatabase({
                    ...database,
                    owners: result.permissions.owners.join(','),
                    admins: result.permissions.admins.join(','),
                    contributors: result.permissions.contributors.join(','),
                    readers: result.permissions.readers.join(','),
                    supervised: result.permissions.supervised.join(','),
                    version: result.version
                });

                alert(TEXT.PermissionUpdated?.[language] || 'Permission updated successfully');
            } else {
                console.error('[DBRECORDS] Error removing permission:', result);
                alert(result.error || TEXT.CannotRemoveLastOwner?.[language] || result.error);
            }
        } catch (error) {
            console.error('[DBRECORDS] Network error removing permission:', error);
            alert('Network error removing permission');
        } finally {
            setUpdatingPermission(false);
        }
    };

    const handleOpenRecordInPage = () => {
        // Open in edit form
        const { index, ...recordData } = selectedViewRecord;
        
        // Initialize fieldItems with record data
        const items = [];
        let itemId = 0;
        
        dbGroup?.fields?.forEach(field => {
            items.push({
                id: itemId++,
                name: field.name,
                type: field.type,
                properties: field.properties || {},
                value: recordData[field.name] || '',
                valid: !field.mandatory || (recordData[field.name] && recordData[field.name] !== '')
            });
        });
        
        setFieldItems(items);
        setEditingRecord({ ...recordData, index });
        handleCloseViewMenu();
    };

    if (!database) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h5" gutterBottom>
                    {TEXT.NoDatabaseSelected?.[language] || 'No database selected'}
                </Typography>
                <Button 
                    variant="contained" 
                    onClick={handleBackToBrowser}
                    startIcon={<ArrowBackIcon />}
                    sx={{ mt: 2 }}
                >
                    {TEXT.BackToBrowser?.[language] || 'Back to Browser'}
                </Button>
            </Box>
        );
    }

    const permission = getUserPermission(database);
    console.log('[DBRECORDS] Current permission:', permission);
    console.log('[DBRECORDS] canEdit result:', canEdit(database));
    console.log('[DBRECORDS] canAdd result:', canAdd(database));
    console.log('[DBRECORDS] canDelete result:', canDelete(database));
    
    // Prepare columns for DataGrid
    const columns = [
        {
            field: 'actions',
            type: 'actions',
            headerName: TEXT.Actions?.[language] || 'Actions',
            width: 100,
            getActions: (params) => {
                const actions = [];
                
                // View action available to all
                actions.push(
                    <GridActionsCellItem
                        icon={<VisibilityIcon />}
                        label={TEXT.ViewRecord?.[language] || 'View'}
                        onClick={(e) => handleViewRecordClick(e, params.row, params.id - 1)}
                        color="info"
                    />
                );
                
                if (canEdit(database)) {
                    actions.push(
                        <GridActionsCellItem
                            icon={<EditIcon />}
                            label={TEXT.EditRecord?.[language] || 'Edit'}
                            onClick={() => handleEditRecordInline(params.row, params.id - 1)}
                            color="primary"
                        />
                    );
                }
                
                if (canDelete(database)) {
                    actions.push(
                        <GridActionsCellItem
                            icon={<DeleteIcon />}
                            label={TEXT.DeleteRecord?.[language] || 'Delete'}
                            onClick={() => handleDeleteRecord(params.id - 1)}
                            color="error"
                        />
                    );
                }
                
                return actions;
            },
        },
        {
            field: 'id',
            headerName: '#',
            width: 70,
            editable: false,
            filterable: false,
        },
        ...(dbGroup?.fields?.map(field => {
            const isEditable = canEdit(database);
            console.log(`[DBRECORDS] Column ${field.name} editable:`, isEditable);
            return {
                field: field.name,
                headerName: field.name + (field.mandatory ? ' *' : ''),
                flex: 1,
                minWidth: 150,
                editable: isEditable,
                type: field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'string',
                valueGetter: (value, row) => {
                    if (field.type === 'date' && value) {
                        return new Date(value);
                    }
                    return value || '';
                },
                valueSetter: (value, row) => {
                    return { ...row, [field.name]: value };
                },
            };
        }) || []),
    ];
    
    // Prepare rows for DataGrid from synchronized Yjs records or fallback
    const activeRecords = (yjsRecords && yjsRecords.length > 0) ? yjsRecords : (database.records || []);
    const rows = activeRecords.map((record, index) => ({
        id: index + 1,
        ...record
    }));

    return (
        <div>
            {/* <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', p: { xs: 1, sm: 2 } }}> */}
            {/* Header */}
            <Paper elevation={2} sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={{ xs: 1, sm: 2 }}>
                    <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}>
                        <Tooltip title={TEXT.BackToBrowser?.[language] || 'Back to Browser'}>
                            <IconButton onClick={handleBackToBrowser} color="primary">
                                <ArrowBackIcon />
                            </IconButton>
                        </Tooltip>
                        <StorageIcon color="primary" sx={{ fontSize: { xs: 24, sm: 32 } }} />
                        <Box>
                            <Typography variant="h5" component="h1" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                                {database.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                {TEXT.BasedOn?.[language] || 'Based on'}: {database.dbGroupName} |
                                {' '}{TEXT.Permission?.[language] || 'Permission'}: {permission.charAt(0).toUpperCase() + permission.slice(1)}
                            </Typography>
                        </Box>
                    </Box>

                    <Box display="flex" gap={{ xs: 0.5, sm: 1 }} alignItems="center" flexWrap="wrap">
                        <Chip
                            label={`${rows.length} ${TEXT.Records?.[language] || 'records'}`}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                        />
                        <Chip
                            label={`v${database.version}`}
                            color="secondary"
                            size="small"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                        />
                        <Chip
                            icon={database.storageType === 'local' ? <ComputerIcon /> : <CloudIcon />}
                            label={database.storageType === 'local' ? TEXT.Local?.[language] : TEXT.Global?.[language]}
                            color={database.storageType === 'local' ? 'default' : 'success'}
                            size="small"
                            sx={{ fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
                        />
                        {/* Yjs Sync Status Badges */}
                        {database.storageType === 'global' && (
                            <>
                                <SyncStatusBadge 
                                    status={syncStatus} 
                                    lastSyncTime={lastSyncTime} 
                                />
                                {pendingChanges && <PendingChangesIndicator pendingChanges={pendingChanges} />}
                            </>
                        )}
                        {canManagePermissions(database) && database.storageType === 'global' && (
                            <Tooltip title={TEXT.ManageAccess?.[language] || 'Manage Access'}>
                                <IconButton 
                                    onClick={() => setPermissionsOpen(true)}
                                    color="secondary"
                                    size="small"
                                >
                                    <PeopleIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        {canAdd(database) && !editingRecord && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAddRecord}
                                startIcon={<AddIcon sx={{ display: { xs: 'none', sm: 'block' } }} />}
                                size="small"
                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                                {TEXT.AddRecord?.[language] || 'Add Record'}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>

            {/* Add/Edit Form */}
            {editingRecord && (
                // <Paper elevation={3} sx={{
                //     p: { xs: 1, sm: 2, md: 3 },
                //     mb: 2,
                //     maxWidth: { xs: '100%', md: 1200 },
                //     mx: { xs: 0, sm: 'auto' },
                //     width: 'inherit'
                // }}>
                <div>
                    <Typography variant="h5" gutterBottom sx={{
                        mb: { xs: 1.5, sm: 2 },
                        fontWeight: 600,
                        fontSize: { xs: '1.1rem', sm: '1.5rem' },
                        px: { xs: 0.5, sm: 0 }
                    }}>
                        {editingRecord.isNew ? TEXT.AddRecord?.[language] : TEXT.EditRecord?.[language]}
                    </Typography>
                    <DragDropContext onDragEnd={() => { }}>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr', md: '1fr 1fr', lg: 'repeat(auto-fit, minmax(280px, 1fr))' },
                            gap: { xs: 1.5, sm: 2, md: 2.5 },
                            mt: { xs: 1, sm: 2 },
                            mb: { xs: 1.5, sm: 2 },
                            px: { xs: 0, sm: 0 }
                        }}>
                            {fieldItems.map((item, index) => (
                                <Box
                                    key={item.id}
                                    sx={{
                                        minHeight: '56px',
                                        display: 'flex',
                                        alignItems: 'stretch',
                                        width: 'auto'
                                    }}
                                >
                                    {fieldSelector(
                                        item, 
                                        fieldItems, 
                                        setFieldItems, 
                                        get_item_by_id, 
                                        get_item_index_by_id, 
                                        () => { }, 
                                        'release',
                                        database ? { databaseName: database.name, dbGroupName: database.dbGroupName } : null
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </DragDropContext>
                    <Box display="flex" gap={{ xs: 1, sm: 1.5 }} mt={{ xs: 1.5, sm: 2 }} flexWrap="wrap" px={{ xs: 0.5, sm: 0 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveRecord}
                            startIcon={<SaveIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                            size="small"
                            sx={{
                                flex: { xs: '1 1 100%', sm: '0 1 auto' },
                                minWidth: { sm: 120 },
                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                            }}
                        >
                            {TEXT.SaveRecord?.[language] || 'Save'}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setEditingRecord(null);
                                setFieldItems([]);
                            }}
                            startIcon={<CancelIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                            size="small"
                            sx={{
                                flex: { xs: '1 1 100%', sm: '0 1 auto' },
                                minWidth: { sm: 120 },
                                fontSize: { xs: '0.8rem', sm: '0.875rem' }
                            }}
                        >
                            {TEXT.Cancel?.[language] || 'Cancel'}
                        </Button>
                    </Box>
                </div>
            )}

            {/* DataGrid */}
            <Paper elevation={2} sx={{ flexGrow: 1, overflow: 'hidden' }}>
                {rows.length === 0 ? (
                    <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                        <Typography variant="body1" color="text.secondary">
                            {TEXT.NoRecords?.[language] || 'No records yet. Add one to get started.'}
                        </Typography>
                    </Box>
                ) : (
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 },
                            },
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        disableRowSelectionOnClick
                        slots={{
                            toolbar: GridToolbar,
                        }}
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 },
                            },
                        }}
                        processRowUpdate={async (newRow, oldRow) => {
                            console.log('[DBRECORDS] processRowUpdate called');
                            console.log('[DBRECORDS] canEdit result:', canEdit(database));
                            if (canEdit(database)) {
                                try {
                                    const result = await handleProcessRowUpdate(newRow, oldRow);
                                    return result;
                                } catch (error) {
                                    console.error('[DBRECORDS] Error in processRowUpdate:', error);
                                    throw error;
                                }
                            }
                            console.log('[DBRECORDS] Edit not allowed, returning old row');
                            return oldRow;
                        }}
                        onProcessRowUpdateError={(error) => {
                            console.error('[DBRECORDS] Error updating row:', error);
                            alert(TEXT.Error?.[language] || 'Error updating record');
                        }}
                        sx={{
                            border: 0,
                            '& .MuiDataGrid-cell:focus': {
                                outline: 'none',
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: 'action.hover',
                            },
                        }}
                    />
                )}
            </Paper>

            {/* Menu for View Record options */}
            <Menu
                anchorEl={viewMenuAnchor}
                open={Boolean(viewMenuAnchor)}
                onClose={handleCloseViewMenu}
            >
                <MenuItem onClick={handleQuickViewRecord}>
                    <PreviewIcon sx={{ mr: 1 }} />
                    {TEXT.QuickView?.[language] || 'Quick View'}
                </MenuItem>
                <MenuItem onClick={handleOpenRecordInPage}>
                    <OpenInNewIcon sx={{ mr: 1 }} />
                    {TEXT.OpenInPage?.[language] || 'Open in Page'}
                </MenuItem>
            </Menu>

            {/* Quick View Record Dialog */}
            <Dialog
                open={quickViewOpen}
                onClose={() => setQuickViewOpen(false)}
                maxWidth="md"
                fullWidth
                fullScreen={{ xs: true, sm: false }}
                PaperProps={{
                    sx: {
                        m: { xs: 0, sm: 2 },
                        maxHeight: { xs: '100%', sm: '90vh' }
                    }
                }}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" alignItems="center">
                            <VisibilityIcon sx={{ mr: 1 }} />
                            {TEXT.RecordDetails?.[language] || 'Record Details'}
                        </Box>
                        {canEdit(database) && (
                            <Button
                                size="small"
                                startIcon={<EditIcon />}
                                onClick={() => {
                                    const { index, ...recordData } = quickViewRecord;

                                    // Initialize fieldItems with record data
                                    const items = [];
                                    let itemId = 0;

                                    dbGroup?.fields?.forEach(field => {
                                        items.push({
                                            id: itemId++,
                                            name: field.name,
                                            type: field.type,
                                            properties: field.properties || {},
                                            value: recordData[field.name] || '',
                                            valid: !field.mandatory || (recordData[field.name] && recordData[field.name] !== '')
                                        });
                                    });

                                    setFieldItems(items);
                                    setEditingRecord({ ...recordData, index });
                                    setQuickViewOpen(false);
                                }}
                            >
                                {TEXT.Edit?.[language] || 'Edit'}
                            </Button>
                        )}
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {quickViewRecord && dbGroup && (() => {
                        // Create all field items for proper context
                        const allFieldItems = dbGroup.fields?.map((field, idx) => ({
                            id: idx,
                            name: field.name,
                            type: field.type,
                            properties: { ...field.properties, disabled: true },
                            value: quickViewRecord[field.name] || '',
                            valid: true
                        })) || [];
                        
                        return (
                            <DragDropContext onDragEnd={() => { }}>
                                <Box>
                                    {allFieldItems.map((item, idx) => (
                                        <Box
                                            key={idx}
                                            sx={{
                                                padding: '8px',
                                                margin: '4px 0',
                                                backgroundColor: '#f9f9f9',
                                                borderRadius: '4px',
                                            }}
                                        >
                                            {fieldSelector(
                                                item, 
                                                allFieldItems, 
                                                () => { }, 
                                                (id) => allFieldItems.find(f => f.id === id), 
                                                (id) => allFieldItems.findIndex(f => f.id === id), 
                                                () => {}, 
                                                'release',
                                                database ? { databaseName: database.name, dbGroupName: database.dbGroupName } : null
                                            )}
                                        </Box>
                                    ))}
                                </Box>
                            </DragDropContext>
                        );
                    })()}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setQuickViewOpen(false)}>
                        {TEXT.Close?.[language] || 'Close'}
                    </Button>
                    {canEdit(database) && (
                        <Button
                            variant="contained"
                            onClick={() => {
                                const { index, ...recordData } = quickViewRecord;

                                // Initialize fieldItems with record data
                                const items = [];
                                let itemId = 0;

                                dbGroup?.fields?.forEach(field => {
                                    items.push({
                                        id: itemId++,
                                        name: field.name,
                                        type: field.type,
                                        properties: field.properties || {},
                                        value: recordData[field.name] || '',
                                        valid: !field.mandatory || (recordData[field.name] && recordData[field.name] !== '')
                                    });
                                });

                                setFieldItems(items);
                                setEditingRecord({ ...recordData, index });
                                setQuickViewOpen(false);
                            }}
                            startIcon={<EditIcon />}
                        >
                            {TEXT.Edit?.[language] || 'Edit'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            {/* Permission Management Dialog */}
            <Dialog
                open={permissionsOpen}
                onClose={() => {
                    setPermissionsOpen(false);
                    setSelectedUser(null);
                    setUserSearchQuery('');
                    setUserSearchResults([]);
                    setSelectedPermissionType('');
                }}
                maxWidth="md"
                fullWidth
                fullScreen={{ xs: true, sm: false }}
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <PeopleIcon sx={{ mr: 1 }} />
                        {TEXT.ManagePermissions?.[language] || 'Manage Permissions'}
                    </Box>
                </DialogTitle>
                <DialogContent dividers>
                    {database?.storageType === 'global' && (
                        <Box>
                            {/* Add User Section */}
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                                    {TEXT.AddUser?.[language] || 'Add User'}
                                </Typography>
                                <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }} mt={2}>
                                    <Autocomplete
                                        sx={{ flex: 1 }}
                                        options={userSearchResults}
                                        getOptionLabel={(option) => `${option.full_name} (${option.email})`}
                                        loading={searchingUsers}
                                        value={selectedUser}
                                        onChange={(event, newValue) => setSelectedUser(newValue)}
                                        inputValue={userSearchQuery}
                                        onInputChange={(event, newInputValue) => {
                                            setUserSearchQuery(newInputValue);
                                            handleSearchUsers(newInputValue);
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={TEXT.SearchUsers?.[language] || 'Search users'}
                                                placeholder={TEXT.SearchUsers?.[language] || 'Search by name, email, or ID...'}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    endAdornment: (
                                                        <>
                                                            {searchingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                                                            {params.InputProps.endAdornment}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        )}
                                        renderOption={(props, option) => (
                                            <li {...props}>
                                                <Box>
                                                    <Typography variant="body1">{option.full_name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {option.email} (ID: {option.uid})
                                                    </Typography>
                                                </Box>
                                            </li>
                                        )}
                                    />
                                    <TextField
                                        select
                                        label={TEXT.SelectPermissionType?.[language] || 'Permission Type'}
                                        value={selectedPermissionType}
                                        onChange={(e) => setSelectedPermissionType(e.target.value)}
                                        sx={{ minWidth: 200 }}
                                    >
                                        <MenuItem value="owners">{TEXT.Owners?.[language] || 'Owners'}</MenuItem>
                                        <MenuItem value="admins">{TEXT.Admins?.[language] || 'Admins'}</MenuItem>
                                        <MenuItem value="contributors">{TEXT.Contributors?.[language] || 'Contributors'}</MenuItem>
                                        <MenuItem value="readers">{TEXT.Readers?.[language] || 'Readers'}</MenuItem>
                                        <MenuItem value="supervised">{TEXT.Supervised?.[language] || 'Supervised'}</MenuItem>
                                    </TextField>
                                    <Button
                                        variant="contained"
                                        onClick={handleAddUserToPermission}
                                        disabled={!selectedUser || !selectedPermissionType || updatingPermission}
                                    >
                                        {TEXT.AddUser?.[language] || 'Add'}
                                    </Button>
                                </Box>
                            </Box>

                            {/* Current Permissions */}
                            {[
                                { key: 'owners', label: TEXT.Owners?.[language] || 'Owners', desc: TEXT.OwnersDescription?.[language] },
                                { key: 'admins', label: TEXT.Admins?.[language] || 'Admins', desc: TEXT.AdminsDescription?.[language] },
                                { key: 'contributors', label: TEXT.Contributors?.[language] || 'Contributors', desc: TEXT.ContributorsDescription?.[language] },
                                { key: 'readers', label: TEXT.Readers?.[language] || 'Readers', desc: TEXT.ReadersDescription?.[language] },
                                { key: 'supervised', label: TEXT.Supervised?.[language] || 'Supervised', desc: TEXT.SupervisedDescription?.[language] }
                            ].map(({ key, label, desc }) => {
                                const userIds = database[key]?.split(',').filter(id => id.trim()) || [];
                                return (
                                    <Box key={key} sx={{ mb: 2 }}>
                                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                            {label} ({userIds.length})
                                        </Typography>
                                        {desc && (
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                                {desc}
                                            </Typography>
                                        )}
                                        {userIds.length === 0 ? (
                                            <Alert severity="info" sx={{ py: 0.5 }}>
                                                {TEXT.NoUsersInGroup?.[language] || 'No users in this group'}
                                            </Alert>
                                        ) : (
                                            <List dense>
                                                {userIds.map((uid) => (
                                                    <ListItem key={uid} sx={{ bgcolor: 'background.default', mb: 0.5, borderRadius: 1 }}>
                                                        <ListItemText
                                                            primary={`User ID: ${uid}`}
                                                            secondary={`UID ${uid}`}
                                                        />
                                                        <ListItemSecondaryAction>
                                                            <IconButton
                                                                edge="end"
                                                                size="small"
                                                                onClick={() => handleRemoveUserFromPermission(uid, key)}
                                                                disabled={updatingPermission || (key === 'owners' && userIds.length === 1)}
                                                                color="error"
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </ListItemSecondaryAction>
                                                    </ListItem>
                                                ))}
                                            </List>
                                        )}
                                        <Divider sx={{ mt: 2 }} />
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setPermissionsOpen(false);
                        setSelectedUser(null);
                        setUserSearchQuery('');
                        setUserSearchResults([]);
                        setSelectedPermissionType('');
                    }}>
                        {TEXT.Close?.[language] || 'Close'}
                    </Button>
                </DialogActions>
            </Dialog>        </div>

    );
};

export default DbRecords;
